import { API_BASE_URL } from '../config';

export interface WorkTask {
  id: number;
  title: string;
  description?: string;
  status: 'neu' | 'erledigt';
  priority: 'hoch' | 'mittel' | 'niedrig';
  due_date?: string;
  project_id: number;
  assigned_to_id: number;
  created_by_id: number;
  attachment_filename?: string;
  attachment_path?: string;
  completion_photo_filename?: string;
  completion_photo_path?: string;
  completion_comment?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
  project_name?: string;
  assigned_to_name?: string;
  created_by_name?: string;
}

export interface WorkTaskCreate {
  title: string;
  description?: string;
  priority: 'hoch' | 'mittel' | 'niedrig';
  due_date?: string;
  project_id: number;
  assigned_to_id: number;
}

export interface WorkTaskUpdate {
  title?: string;
  description?: string;
  priority?: 'hoch' | 'mittel' | 'niedrig';
  due_date?: string;
  project_id?: number;
  assigned_to_id?: number;
  status?: 'neu' | 'erledigt';
  completion_comment?: string;
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
  };
};

export const getWorkTasks = async (): Promise<WorkTask[]> => {
  const response = await fetch(`${API_BASE_URL}/work-tasks`, {
    headers: getAuthHeaders(),
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch work tasks');
  }
  
  return response.json();
};

export const getWorkTask = async (id: number): Promise<WorkTask> => {
  const response = await fetch(`${API_BASE_URL}/work-tasks/${id}`, {
    headers: getAuthHeaders(),
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch work task');
  }
  
  return response.json();
};

export const createWorkTask = async (
  taskData: WorkTaskCreate,
  attachment?: File
): Promise<WorkTask> => {
  const formData = new FormData();
  
  formData.append('title', taskData.title);
  if (taskData.description) formData.append('description', taskData.description);
  formData.append('priority', taskData.priority);
  if (taskData.due_date) formData.append('due_date', taskData.due_date);
  formData.append('project_id', taskData.project_id.toString());
  formData.append('assigned_to_id', taskData.assigned_to_id.toString());
  
  if (attachment) {
    formData.append('attachment', attachment);
  }
  
  const response = await fetch(`${API_BASE_URL}/work-tasks`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: formData,
  });
  
  if (!response.ok) {
    throw new Error('Failed to create work task');
  }
  
  return response.json();
};

export const updateWorkTask = async (
  id: number,
  taskData: WorkTaskUpdate
): Promise<WorkTask> => {
  const response = await fetch(`${API_BASE_URL}/work-tasks/${id}`, {
    method: 'PUT',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(taskData),
  });
  
  if (!response.ok) {
    throw new Error('Failed to update work task');
  }
  
  return response.json();
};

export const completeWorkTask = async (
  id: number,
  completionComment?: string,
  completionPhoto?: File
): Promise<WorkTask> => {
  const formData = new FormData();
  
  if (completionComment) {
    formData.append('completion_comment', completionComment);
  }
  
  if (completionPhoto) {
    formData.append('completion_photo', completionPhoto);
  }
  
  const response = await fetch(`${API_BASE_URL}/work-tasks/${id}/complete`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: formData,
  });
  
  if (!response.ok) {
    throw new Error('Failed to complete work task');
  }
  
  return response.json();
};

export const deleteWorkTask = async (id: number): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/work-tasks/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  
  if (!response.ok) {
    throw new Error('Failed to delete work task');
  }
};

export const getNewTasksCount = async (): Promise<{ count: number }> => {
  const response = await fetch(`${API_BASE_URL}/work-tasks/count/new`, {
    headers: getAuthHeaders(),
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch new tasks count');
  }
  
  return response.json();
};

export const downloadAttachment = async (taskId: number): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/work-tasks/${taskId}/download-attachment`, {
    headers: getAuthHeaders(),
  });
  
  if (!response.ok) {
    throw new Error('Failed to download attachment');
  }
  
  // Get filename from Content-Disposition header or use default
  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = 'attachment';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }
  
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export const downloadCompletionPhoto = async (taskId: number): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/work-tasks/${taskId}/download-completion-photo`, {
    headers: getAuthHeaders(),
  });
  
  if (!response.ok) {
    throw new Error('Failed to download completion photo');
  }
  
  // Get filename from Content-Disposition header or use default
  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = 'completion_photo.jpg';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }
  
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export const getAttachmentUrl = (taskId: number): string => {
  const token = localStorage.getItem('token');
  return `${API_BASE_URL}/work-tasks/${taskId}/attachment?token=${token}`;
};

export const getCompletionPhotoUrl = (taskId: number): string => {
  const token = localStorage.getItem('token');
  return `${API_BASE_URL}/work-tasks/${taskId}/completion-photo?token=${token}`;
};
